import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../CustomButton";
import CustomInput from "../CustomInput";
import ModalLayout from "../layouts/ModalLayout";
import classesApis from "../../api/classesApis";
import { ClassFormData } from "../../types/classesTypes";
import { AppDispatch, RootState } from "../../store";
import { fetchClasses } from "../../features/classes/ClassesSlice";
import { useLocation, useParams } from "react-router-dom";
import AcademicCycleSelect from "../common-input/AcademicCycle";

interface Props {
  onClose?: () => void;
  onSuccess?: () => void;
}

function AddClasses({ onClose, onSuccess }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { classId: urlClassId } = useParams();
  const selectedClass = location.state?.selectedClass;
  const { filters, page, size } = useSelector(
    (state: RootState) => state.classes,
  );

  const { schoolBranch } = useSelector((state: RootState) => state.access);

  const branchCode = schoolBranch?.schoolBranchCode;

  const [formData, setFormData] = useState<ClassFormData>({
    className: selectedClass?.className || "",
    academicCycleId: selectedClass?.academicCycleId || "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const classId =
    selectedClass?.classId || (urlClassId ? Number(urlClassId) : undefined);

  const isEdit = !!selectedClass && !!classId;

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.className || !formData.academicCycleId) {
      toast.error("Please fill all required fields");
      return;
    }

    if (isEdit && !classId) {
      toast.error("Invalid class data - missing class ID");
      return;
    }

    setIsLoading(true);
    try {
      const body = {
        className: formData.className,
        academicCycleId: Number(formData.academicCycleId),
        branchCode,
      };

      const res = isEdit
        ? await classesApis.updateClass(body, classId!)
        : await classesApis.createClass(body);

      if (res?.status === 1) {
        toast.success(
          isEdit ? "Class updated successfully" : "Class created successfully",
        );

        dispatch(fetchClasses({ page, size, filters, branchCode }));

        onSuccess?.();
        onClose?.();
      } else {
        toast.error(res?.error?.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalLayout onClose={onClose}>
      <form className="flex flex-col gap-8 max-w-xl" onSubmit={handleSubmit}>
        <CustomInput
          name="className"
          label="Class Name"
          type="text"
          placeholder="Enter class name"
          value={formData.className}
          onChange={handleInputChange}
          inputClassName="w-full"
        />

        <AcademicCycleSelect
          name="academicCycleId"
          label="Academic Cycle"
          value={formData?.academicCycleId}
          placeholder="Select academic cycle"
          onChange={handleInputChange}
        />

        <div className="flex justify-end gap-5">
          <CustomButton
            color="bg-neutral-200"
            text="Cancel"
            onClick={onClose}
            disabled={isLoading}
          />
          <CustomButton
            color="bg-primary-500"
            text={
              isLoading ? "Saving..." : isEdit ? "Update Class" : "Save Class"
            }
            type="submit"
            disabled={isLoading}
          />
        </div>
      </form>
    </ModalLayout>
  );
}

export default AddClasses;
